import { Box, Button, CircularProgress, Link, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useDeleteDocument, useRetryDocumentOcr, useUpsertDocument } from "core/documents/hooks";
import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { erroredAxiosResponse } from "utils";
import ValidationModal from "./ValidationModal/ValidationModal";
import { CloudUpload } from "@mui/icons-material";
import useConfirmationDialog from "hooks/useConfirmationDialog";
import DocumentFileCard from "./DocumentFileCard";

type Props = {
  document: any;
  tempWorkerId: any;
  label: string;
  kind: string;
}

const DocumentFiles = ({ document, tempWorkerId, label, kind }: Props) => {
  const [validationModalOpen, setValidationModalOpen] = useState(false);
  const [ocrRetryAvailable, setOcrRetryAvailable] = useState(false);
  const queryClient = useQueryClient();
  const { confirmationDialog, confirm, closeConfirmationDialog } = useConfirmationDialog();

  useEffect(() => {
    if (document.ocr_job_status !== 'pending' || !document.ocr_job_enqueued_at) {
      setOcrRetryAvailable(false);
      return;
    }

    const retryAt = new Date(document.ocr_job_enqueued_at).getTime() + 5 * 60 * 1000;
    const updateAvailability = () => setOcrRetryAvailable(Date.now() >= retryAt);
    updateAvailability();

    const timeout = window.setTimeout(updateAvailability, Math.max(retryAt - Date.now(), 0));
    return () => window.clearTimeout(timeout);
  }, [document.ocr_job_enqueued_at, document.ocr_job_status]);
  
  const openValidationModal = () => {
    queryClient.invalidateQueries({ queryKey: ['temp_workers', tempWorkerId, 'documents'] });
    setValidationModalOpen(true);
  };

  const closeValidationModal = () => {
    setValidationModalOpen(false);
  };

  const uploadFile = useUpsertDocument(tempWorkerId, {
    onSuccess: () => {
      toast.success('Le fichier a été uploadé');
    },
    onError: (responseError: any) => toast.error(erroredAxiosResponse(responseError, 'Le document n\'a pas pu être uploadé')),
  });

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (document.ocr_job_status === 'pending') {
      return;
    }

    const file = e.target.files?.[0];
    if (file) {
      const mutateData = {tempWorkerId: tempWorkerId, documentId: document?.id, key: document.key, file: file};

      return uploadFile.mutate(mutateData);
    }
  }, [document, tempWorkerId, uploadFile]);

  const deleteFileMutation = useDeleteDocument(tempWorkerId, {
    onSuccess: () => {
        toast.success('Le fichier a été supprimé');
    },
    onError: (responseError: any) => toast.error(erroredAxiosResponse(responseError, 'Le document n\'a pas pu être supprimé')),
  });

  const retryOcrMutation = useRetryDocumentOcr(tempWorkerId, {
    onSuccess: () => {
      setOcrRetryAvailable(false);
      toast.success("La lecture automatique a été relancée");
    },
    onError: (responseError: any) => toast.error(erroredAxiosResponse(responseError, "La lecture automatique n'a pas pu être relancée")),
  });

  const onDeleteFile = (file: any) => {
    confirm({
      title: 'Supprimer le document',
      description: 'Voulez-vous vraiment supprimer le document ?',
      onConfirm: () => {
        closeConfirmationDialog();
        deleteFileMutation.mutate({tempWorkerId: tempWorkerId, id: document.id, file_id: file.id});
      },
    });
  };

  return (
    <Box>
      {confirmationDialog}
      {kind !== 'free_form' && <ValidationModal open={validationModalOpen} onClose={closeValidationModal} document={document} tempWorkerId={tempWorkerId} />}
      <Box sx={{
        backgroundColor: 'primary.100',
        padding: '1rem',
        border: '1px dashed',
        borderColor: 'primary.300',
        borderRadius: '0.5rem',
      }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2} mb={3}>
          <Stack direction="row" gap={3} alignItems="center" flexWrap="wrap">
            <Typography variant="h6">Document(s)</Typography>
            {(document.validation_state === 'pending' || document.files.length === 0) && (
              <label htmlFor={`upload-file-${document.id}`} onClick={(e) => {
                if (document.ocr_job_status === 'pending') {
                  e.preventDefault();
                  toast.error("Le document est en cours de traitement, veuillez attendre la fin du traitement avant d'en importer un nouveau");
                  return;
                }
              }}>
                <input
                    style={{ display: 'none' }}
                    id={`upload-file-${document.id}`}
                    type="file"
                    disabled={uploadFile.isLoading}
                    onChange={handleFileChange}
                />
                <Stack direction="row" gap={1} sx={{ cursor: 'pointer', alignItems: 'center' }}>
                  {uploadFile.isLoading ? (
                    <CircularProgress size={20} />
                  ) : (
                    <>
                      <CloudUpload sx={{ color: 'secondary.main', fontSize: '20px' }} />
                      <Typography variant="body1" sx={{ color: 'secondary.main', textDecoration: 'underline' }}>Importer un document</Typography>
                    </>
                  )}
                </Stack>
              </label>
            )}
          </Stack>
          {kind !== 'free_form' && document.files.length > 0 && (
            <Stack direction="column" sx={{ ml: 'auto' }}>
              <Button
                variant="contained"
                color="secondary"
                disableElevation
                sx={{ px: 2, height: '40px', fontSize: '0.875rem', fontWeight: 600, borderRadius: '10px', whiteSpace: 'nowrap' }}
                onClick={openValidationModal}
                disabled={document.ocr_job_status === 'pending'}
              >
                {document.validation_state === 'pending' ? 'Vérifier le(s) document(s)' : 'Voir le(s) document(s)'}
              </Button>
              {document.ocr_job_status === 'pending' && (
                <Stack alignItems="flex-end" mt={0.5}>
                  {ocrRetryAvailable ? (
                    <Typography variant="body1" sx={{ color: 'grey.700', fontSize: '0.75rem', fontStyle: 'italic' }}>
                      Il semblerait que l'OCR soit bloqué.{' '}
                      <Link
                        component="button"
                        type="button"
                        disabled={retryOcrMutation.isLoading}
                        onClick={() => {
                          if (!retryOcrMutation.isLoading) {
                            retryOcrMutation.mutate({ tempWorkerId, documentId: document.id });
                          }
                        }}
                        sx={{
                          color: 'secondary.main',
                          cursor: retryOcrMutation.isLoading ? 'default' : 'pointer',
                          fontFamily: 'inherit',
                          fontSize: 'inherit',
                          fontStyle: 'inherit',
                          lineHeight: 'inherit',
                          padding: 0,
                          verticalAlign: 'baseline',
                        }}
                      >
                        {retryOcrMutation.isLoading ? 'Relance...' : "Relancer l'OCR"}
                      </Link>
                    </Typography>
                  ) : (
                    <Typography variant="body1" sx={{ color: 'grey.700', fontSize: '0.75rem', fontStyle: 'italic' }}>
                      Traitement en cours...
                    </Typography>
                  )}
                </Stack>
              )}
            </Stack>
          )}
        </Stack>
        <Stack direction="row" gap={2} alignItems="stretch" flexWrap="wrap">
          {document.files.map((file: any) => (
            <DocumentFileCard
              key={file.id}
              file={file}
              document={document}
              label={label}
              kind={kind}
              onDeleteFile={onDeleteFile}
            />
          ))}
        </Stack>
      </Box>
    </Box>
  );
};

export default DocumentFiles;
